export type Granularity = "raw" | "2m" | "5m" | "15m" | "h" | "d" | "w" | "m" | "y";

export interface TimeRange {
  start: number;
  end: number;
  gran: Granularity;
  preset: TimeRangePreset;
}

export type TimeRangePreset =
  | "live-1h"
  | "live-6h"
  | "today"
  | "24h"
  | "7d"
  | "30d"
  | "ytd"
  | "custom";

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export function rangeFromPreset(preset: TimeRangePreset, anchor = Date.now()): TimeRange {
  switch (preset) {
    case "live-1h":
      return { start: anchor - HOUR, end: anchor, gran: "raw", preset };
    case "live-6h":
      return { start: anchor - 6 * HOUR, end: anchor, gran: "15m", preset };
    case "today": {
      const startOfDay = new Date(anchor); startOfDay.setHours(0, 0, 0, 0);
      return { start: startOfDay.getTime(), end: anchor, gran: "h", preset };
    }
    case "24h":
      // 15-min granularity gives ~96 sample points across a day —
      // smooth enough to read trends, dense enough to spot transitions.
      // 1-hour was too coarse for operator inspection.
      return { start: anchor - DAY, end: anchor, gran: "15m", preset };
    case "7d":
      return { start: anchor - 7 * DAY, end: anchor, gran: "h", preset };
    case "30d":
      return { start: anchor - 30 * DAY, end: anchor, gran: "d", preset };
    case "ytd": {
      const jan1 = new Date(anchor); jan1.setMonth(0, 1); jan1.setHours(0, 0, 0, 0);
      return { start: jan1.getTime(), end: anchor, gran: "d", preset };
    }
    case "custom":
      return { start: anchor - 24 * HOUR, end: anchor, gran: "h", preset };
  }
}

export const PRESETS: { key: TimeRangePreset; label: string }[] = [
  { key: "live-1h", label: "Last hour" },
  { key: "live-6h", label: "Last 6 h" },
  { key: "today", label: "Today" },
  { key: "24h", label: "24 h" },
  { key: "7d", label: "7 d" },
  { key: "30d", label: "30 d" },
  { key: "ytd", label: "Year-to-date" },
];

/**
 * Pick a sane default granularity for a span. When `allowed` is given the
 * result is snapped to that list (falling back to a mid-list entry), so
 * pages with restricted granularity sets never receive one they can't
 * render. Shared by TimeRangeBar's from/to edits and the chart-zoom →
 * range sync (HIST-05).
 */
export function autoGran(spanMs: number, allowed?: Granularity[]): Granularity {
  const hours = spanMs / 3_600_000;
  const target: Granularity =
    hours <= 4 ? "raw"
    : hours <= 36 ? "15m"
    : hours <= 7 * 24 ? "h"
    : hours <= 90 * 24 ? "d"
    : hours <= 365 * 24 ? "w"
    : "m";
  if (!allowed || allowed.includes(target)) return target;
  return allowed[Math.min(2, allowed.length - 1)];
}

// Note on `raw`: the edge EMS uplinks once every ~2 minutes, so "raw"
// here means "every uplink sample" — NOT sub-second. We label it so
// operators don't mistake the curve for second-level resolution.
export const GRAN_LABEL: Record<Granularity, string> = {
  raw: "raw (~2 min uplink)",
  "2m": "2 min",
  "5m": "5 min",
  "15m": "15 min",
  h: "1 hour",
  d: "1 day",
  w: "1 week",
  m: "1 month",
  y: "1 year",
};
